class ZvBundleProducts extends HTMLElement {
  connectedCallback() {
    this.section = this.closest('.zv-bundle-products');
    if (!this.section) return;

    this.cards = [...this.section.querySelectorAll('[data-bundle-card]')].filter(
      (card) => card.dataset.variantId
    );
    this.stickyBar = this.section.querySelector('[data-sticky-bar]');
    this.stickyVisible = false;

    this.bindCardEvents();
    this.bindStickyEvents();
    this.bindScrollVisibility();

    const initial =
      this.cards.find((card) => card.classList.contains('featured')) || this.cards[0];

    if (initial) {
      this.selectCard(initial, { scroll: false });
    }
  }

  bindCardEvents() {
    this.cards.forEach((card) => {
      card.addEventListener('click', (event) => {
        if (event.target.closest('[data-pick-button]')) return;
        this.selectCard(card);
      });

      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        this.selectCard(card);
      });

      const pickBtn = card.querySelector('[data-pick-button]');
      if (pickBtn) {
        pickBtn.addEventListener('click', (event) => {
          event.stopPropagation();
          this.selectCard(card);
        });
      }

    });
  }

  bindStickyEvents() {
    if (!this.stickyBar) return;

    const stickyAdd = this.stickyBar.querySelector('[data-sticky-add]');
    if (stickyAdd) {
      stickyAdd.addEventListener('click', () => this.addSelectedToCart(stickyAdd));
    }
  }

  bindScrollVisibility() {
    if (!this.stickyBar) return;

    this.firstFoldInView = true;
    this.footerInView = false;

    this.updateStickyVisibility = () => {
      const shouldShow =
        !this.firstFoldInView && !this.footerInView && !!this.selectedCard;
      this.stickyBar.classList.toggle('show', shouldShow);
      this.stickyVisible = shouldShow;
    };

    const anchorId = this.section.dataset.firstFoldAnchor;
    const firstFoldEl = anchorId ? document.getElementById(anchorId) : null;

    if (firstFoldEl) {
      this.firstFoldObserver = new IntersectionObserver(
        ([entry]) => {
          this.firstFoldInView = entry.isIntersecting;
          this.updateStickyVisibility();
        },
        { threshold: 0 }
      );
      this.firstFoldObserver.observe(firstFoldEl);
    } else {
      this.onScroll = () => {
        this.firstFoldInView = window.scrollY < window.innerHeight;
        this.updateStickyVisibility();
      };

      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.onScroll();
    }

    const footerId = this.section.dataset.footerAnchor;
    const footerEl =
      (footerId && document.getElementById(footerId)) ||
      document.querySelector('footer') ||
      document.querySelector('.shopify-section-group-footer-group');

    if (footerEl) {
      this.footerObserver = new IntersectionObserver(
        ([entry]) => {
          this.footerInView = entry.isIntersecting;
          this.updateStickyVisibility();
        },
        { threshold: 0 }
      );
      this.footerObserver.observe(footerEl);
    }
  }

  disconnectedCallback() {
    if (this.firstFoldObserver) {
      this.firstFoldObserver.disconnect();
    }
    if (this.footerObserver) {
      this.footerObserver.disconnect();
    }
    if (this.onScroll) {
      window.removeEventListener('scroll', this.onScroll);
    }
  }

  selectCard(card, options = {}) {
    if (!card || !card.dataset.variantId) return;

    this.selectedCard = card;

    this.cards.forEach((item) => {
      const isSelected = item === card;
      item.classList.toggle('featured', isSelected);
      item.setAttribute('aria-pressed', isSelected ? 'true' : 'false');

      const pickBtn = item.querySelector('[data-pick-button]');
      if (!pickBtn) return;

      const selectedLabel = pickBtn.dataset.selectedLabel || 'Selectat';
      const pickLabel = pickBtn.dataset.pickLabel || 'Alege acest pachet';
      pickBtn.querySelector('[data-pick-label]').textContent = isSelected
        ? selectedLabel
        : pickLabel;
    });

    this.updateStickyBar(card);

    if (this.updateStickyVisibility) {
      this.updateStickyVisibility();
    }

    if (options.scroll) {
      card.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }

  updateStickyBar(card) {
    if (!this.stickyBar) return;

    const nameEl = this.stickyBar.querySelector('[data-sticky-name]');
    const capsEl = this.stickyBar.querySelector('[data-sticky-capsules]');
    const nowEl = this.stickyBar.querySelector('[data-sticky-price]');
    const oldEl = this.stickyBar.querySelector('[data-sticky-compare]');
    const thumbEl = this.stickyBar.querySelector('[data-sticky-thumb]');

    if (nameEl) nameEl.textContent = card.dataset.stickyTitle || card.dataset.productTitle || '';

    if (capsEl) {
      const freeLabel = card.dataset.freeLabel || '';
      const units = card.dataset.units;
      const capsules = card.dataset.capsules;
      let html = '';

      if (freeLabel) {
        html += `<span class="gp">${freeLabel}</span>`;
      }

      if (capsules) {
        html += `${html ? ' · ' : ''}${units} × ${capsules} capsule`;
      }

      capsEl.innerHTML = html;
      capsEl.classList.toggle('is-hidden', html === '');
    }

    if (nowEl) nowEl.textContent = card.dataset.priceFormatted || '';
    if (oldEl) {
      const hasCompare = card.dataset.comparePriceFormatted;
      oldEl.textContent = hasCompare || '';
      oldEl.classList.toggle('is-hidden', !hasCompare);
    }

    if (thumbEl) {
      const image = card.dataset.image;
      if (image) {
        thumbEl.innerHTML = `<img src="${image}" alt="" width="48" height="48" loading="lazy">`;
      } else {
        thumbEl.innerHTML = '';
      }
    }

    const stickyAdd = this.stickyBar.querySelector('[data-sticky-add]');
    if (stickyAdd) {
      stickyAdd.dataset.variantId = card.dataset.variantId;
      stickyAdd.disabled = card.dataset.available !== 'true';
    }
  }

  async addSelectedToCart(button) {
    const card = this.selectedCard;
    if (!card) return;

    const variantId = button.dataset.variantId || card.dataset.variantId;
    if (!variantId || card.dataset.available !== 'true') return;

    const originalText = button.querySelector('[data-add-label]');
    const labelNode = originalText || button;
    const previousLabel = labelNode.textContent;

    button.disabled = true;
    if (originalText) {
      originalText.textContent = button.dataset.loadingLabel || 'Se adaugă…';
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] }),
      });

      if (!response.ok) {
        throw new Error('Add to cart failed');
      }

      const cartData = await response.json();

      this.section.dispatchEvent(
        new CustomEvent('zv:cart:added', {
          bubbles: true,
          detail: { variantId, cart: cartData },
        })
      );

      document.documentElement.dispatchEvent(
        new CustomEvent('cart:refresh', { bubbles: true, detail: { cart: cartData } })
      );

      if (typeof window.publish === 'function' && window.PUB_SUB_EVENTS?.cartUpdate) {
        window.publish(window.PUB_SUB_EVENTS.cartUpdate, {
          source: 'zv-bundle-products',
          cartData,
        });
      }

      if (originalText) {
        originalText.textContent = button.dataset.successLabel || 'Adăugat!';
      }

      window.setTimeout(() => {
        if (originalText) originalText.textContent = previousLabel;
        button.disabled = card.dataset.available !== 'true';
      }, 1800);
    } catch (error) {
      console.error('[zv-bundle-products]', error);
      if (originalText) originalText.textContent = previousLabel;
      button.disabled = false;
    }
  }
}

if (!customElements.get('zv-bundle-products')) {
  customElements.define('zv-bundle-products', ZvBundleProducts);
}
